import { ApolloError, gql } from '@apollo/client'
import * as React from 'react'
import styled from 'styled-components'
import { Link } from '../../../../common/src/components/Link'
import { BodyTextSpan, BodyMonoText } from '../../../../common/src/components/core'
import { ColorName, Colors } from '../../../../common/src/constants'
import { handleError } from '../../../../common/src/data/errors'
import { plural } from '../../../../common/src/format'
import { ConfirmationStatus, ShowingStatus, CalResponseStatus } from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'

const updateShowingMutation = gql`
  mutation UpdateShowing($showingId: Int!, $showingStatus: ShowingStatus!) {
    updateShowing(showingId: $showingId, showingStatus: $showingStatus) {
      id
    }
  }
`

function updateShowing(showingId: number, showingStatus: string) {
  return apolloWebClient
    .mutate({
      mutation: updateShowingMutation,
      variables: {
        showingId,
        showingStatus,
      },
    })
    .catch((err: ApolloError) => handleError(err))
}

export class ShowingRejectButton extends React.PureComponent<{
  showing: {
    id: number
    leasingTaskId: number
    timeStart: string
    showingContacts: { confirmationStatus: ConfirmationStatus }[]
  }
  status: ShowingStatus
}> {
  render() {
    const { status } = this.props

    // Choose showingStatus for the whole showing
    const isRejected = status === 'REJECTED'
    return (
      <BodyTextSpan>
        <RejectLink color={isRejected ? 'coral' : 'silver'} onClick={this.toggleRejectedBound}>
          {isRejected ? 'Rejected' : 'Reject'}
        </RejectLink>
      </BodyTextSpan>
    )
  }

  private toggleRejectedBound = () => this.setRejected(this.props.status !== 'REJECTED')

  private setRejected(isRejected: boolean) {
    const { showing } = this.props
    const newStatus = isRejected ? 'REJECTED' : 'CREATED'
    const numToCancel = showing.showingContacts.filter(sc =>
      ['ACCEPTED', 'CONFIRM_SENT', 'CONFIRMED'].includes(sc.confirmationStatus)
    ).length
    const numOffered = showing.showingContacts.filter(sc => sc.confirmationStatus === 'OFFERED').length

    if (isRejected) {
      let msg = 'Are you sure you want to cancel this showing?'
      if (numOffered > 0 || numToCancel > 0) {
        msg += ' We already offered this showing to people.'
      }
      if (numToCancel > 0) {
        const strToCancel = plural(numToCancel, 'person')
        msg += ` We'd have to cancel ${strToCancel} who already said yes.`
      }
      if (!window.confirm(msg)) {
        return
      }
    }

    updateShowing(showing.id, newStatus).catch(handleError)
  }
}

export class ConfirmationStatusSpan extends React.PureComponent<{
  showingConfStatus: ConfirmationStatus
  calRespStatus?: CalResponseStatus | null
  style?: React.CSSProperties
}> {
  render() {
    const { showingConfStatus, calRespStatus, style } = this.props
    const tc = getStatusTextColor(showingConfStatus, calRespStatus || undefined)

    const tooltip = showingConfStatus + (calRespStatus ? ', calendar status ' + calRespStatus : '')
    return (
      <BodyMonoText as="span" color={tc.color} title={tooltip} style={style}>
        {tc.text}
      </BodyMonoText>
    )
  }
}

function getStatusTextColor(
  showingConfStatus: ConfirmationStatus,
  calRespStatus?: CalResponseStatus
): { text: string; color: ColorName } {
  if (calRespStatus) {
    if (calRespStatus === 'ACCEPTED' && showingConfStatus === 'CONFIRMED') {
      return { text: 'CONFIRMED', color: 'mint' }
    } else if (calRespStatus === 'DECLINED' && showingConfStatus === 'CANCEL_BY_THEM') {
      return { text: 'DECLINED', color: 'coral' }
    } else if (calRespStatus === 'NEEDS_ACTION' && showingConfStatus === 'OFFERED') {
      return { text: 'OFFERED', color: 'midway' }
    } else if (calRespStatus === 'UNKNOWN' && showingConfStatus === 'OFFERED') {
      return { text: 'OFFERED', color: 'midway' }
    } else if (calRespStatus === 'TENTATIVE' && showingConfStatus === 'CONFIRMED') {
      return { text: 'TENTATIVE', color: 'mint' }
    } else if (showingConfStatus === 'NO_SHOW') {
      return { text: 'NO_SHOW', color: 'coral' }
    } else if (showingConfStatus === 'CONFIRMED_NOSYNC') {
      return { text: 'CONFIRMED*', color: 'mint' }
    } else if (showingConfStatus === 'CANCEL_BY_THEM_NOSYNC') {
      return { text: 'DECLINED*', color: 'coral' }
    }
    // Invalid combination of showing confirmation status and calendar response status
    return { text: showingConfStatus, color: 'midway' }
  }

  return {
    text: getStatusText(showingConfStatus),
    color: getStatusColor(showingConfStatus),
  }
}

function getStatusColor(status: ConfirmationStatus): ColorName {
  if (status === 'NO_SHOW') {
    return 'coral'
  } else if (status === 'CONFIRMED') {
    return 'mint'
  } else if (status === 'ACCEPTED') {
    return 'lemon'
  } else if (status === 'CONFIRM_SENT') {
    return 'lemon'
  } else {
    return 'midway'
  }
}

function getStatusText(status: ConfirmationStatus): string {
  if (status === 'CONFIRM_SENT') {
    return 'CONF. SENT'
  } else if (status === 'CONFIRMED_NOSYNC') {
    return 'CONFIRMED*'
  } else if (status === 'NO_SHOW') {
    return 'NO SHOW'
  } else if (status === 'CANCEL_BY_US') {
    return 'CANCEL/US'
  } else if (status === 'CANCEL_BY_THEM') {
    return 'CAN./THEM'
  } else if (status === 'CANCEL_BY_THEM_NOSYNC') {
    return 'CAN./THEM*'
  } else if (status === 'DISQUALIFIED') {
    return 'DISQUAL.'
  }
  if (status.includes('_')) {
    return status.replace('_', ' ')
  }
  return status
}

export const RejectLink = styled(Link)`
  &:hover {
    color: ${Colors.coral};
  }
`
